import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../auth/AuthContext";
import { formatApiError, track } from "../lib/api";
import { SEO } from "../components/SEO";

export default function AuthPage() {
  const { t } = useLang();
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = mode === "login" ? await login(email, password) : await register(name, email, password);
      if (mode === "register") track("sign_up", { method: "email" });
      navigate(user.role === "admin" ? "/admin" : "/panel");
    } catch (err) {
      toast.error(formatApiError(err, t("form_error")));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full h-13 py-3.5 px-5 rounded-2xl bg-mist border border-black/10 text-sm text-ink placeholder:text-sub/70 focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition-shadow";

  return (
    <div className="min-h-screen bg-white">
      <SEO title={t("auth_login_title")} description={t("auth_login_title")} path="/giris" noindex />
      <Navbar />
      <div className="pt-32 pb-24 px-6 flex justify-center">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-3xl bg-ink text-white flex items-center justify-center font-heading font-bold text-lg mx-auto">2D</div>
            <h1 className="mt-5 font-heading text-3xl font-bold tracking-tight text-ink" data-testid="auth-title">
              {mode === "login" ? t("auth_login_title") : t("auth_register_title")}
            </h1>
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <input data-testid="auth-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder={t("form_name")} className={inputCls} />
            )}
            <input data-testid="auth-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("form_email")} className={inputCls} />
            <input data-testid="auth-password" required type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("auth_password")} className={inputCls} />
            <button data-testid="auth-submit" disabled={busy} className="w-full h-13 py-3.5 rounded-full bg-brand text-white font-heading font-semibold text-sm hover:bg-branddeep disabled:opacity-60 transition-colors">
              {busy ? "..." : mode === "login" ? t("auth_login_btn") : t("auth_register_btn")}
            </button>
          </form>
          <div className="mt-4 flex items-center gap-3 text-[11px] text-sub">
            <span className="flex-1 h-px bg-black/10" />{t("auth_or")}<span className="flex-1 h-px bg-black/10" />
          </div>
          <button
            data-testid="google-login-btn"
            type="button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/panel";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="mt-4 w-full h-13 py-3.5 rounded-full bg-white border border-black/10 font-heading font-semibold text-sm text-ink hover:bg-mist transition-colors flex items-center justify-center gap-2.5"
          >
            <svg width="17" height="17" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            {t("auth_google")}
          </button>
          <button
            data-testid="auth-switch-mode"
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="mt-6 w-full text-center text-sm text-brand font-semibold hover:underline"
          >
            {mode === "login" ? t("auth_to_register") : t("auth_to_login")}
          </button>
        </motion.div>
      </div>
    </div>
  );
}
